import { useCallback, useMemo, useState } from 'react';

import { hasBlockingQuickBooksWarnings } from '@/modules/finance/common';

import { MonthlySalaryActions } from '../../components/generate-bills-table/generate-bills-table.types';
import { GenerateBillListItem } from '../../types';

/** The raw per-member overrides, split out so the page can read `hasOverrides` early. */
export interface MonthlySalaryOverrides {
  /** Whether salary mode is on for a given member id. */
  isEnabledFor: (memberId: string) => boolean;
  actions: MonthlySalaryActions;
  /** True when the user has changed any member's mode (feeds the unsaved-changes guard). */
  hasOverrides: boolean;
  reset: () => void;
  setEnabledFor: (memberIds: string[], isEnabled: boolean) => void;
}

export interface MonthlySalaryMode extends MonthlySalaryOverrides {
  /** Members the bulk toggle can flip: eligible AND connected to QuickBooks. */
  eligibleMemberIds: string[];
  isAllEnabled: boolean;
  toggleAll: (isEnabled: boolean) => void;
}

/**
 * Owns the per-member monthly-salary billing mode overrides.
 *
 * Kept in page state rather than the form because it is a row-composition decision: it controls
 * whether the derived salary-adjustment sub-row exists at all. Members are never switched on
 * automatically — only by explicit user action.
 *
 * Declared before the loaded members are available so `hasOverrides` can feed the page's
 * unsaved-changes guard; `useMonthlySalaryMode` layers the member-derived bulk state on top.
 */
export const useMonthlySalaryOverrides = (): MonthlySalaryOverrides => {
  const [enabledByMember, setEnabledByMember] = useState<Record<string, boolean>>({});

  const toggleMonthlySalary = useCallback((memberId: string, isEnabled: boolean) => {
    setEnabledByMember(prev => ({ ...prev, [memberId]: isEnabled }));
  }, []);

  const setEnabledFor = useCallback((memberIds: string[], isEnabled: boolean) => {
    setEnabledByMember(prev => ({
      ...prev,
      ...Object.fromEntries(memberIds.map(memberId => [memberId, isEnabled])),
    }));
  }, []);

  return {
    isEnabledFor: useCallback(
      (memberId: string) => enabledByMember[memberId] ?? false,
      [enabledByMember]
    ),
    actions: useMemo(() => ({ toggleMonthlySalary }), [toggleMonthlySalary]),
    hasOverrides: Object.keys(enabledByMember).length > 0,
    reset: useCallback(() => setEnabledByMember({}), []),
    setEnabledFor,
  };
};

interface UseMonthlySalaryModeParams {
  overrides: MonthlySalaryOverrides;
  /** Loaded members, used to derive which ones the bulk toggle can actually flip. */
  members: GenerateBillListItem[] | undefined;
}

/** Adds the bulk-toggle state, which needs the loaded members, to the raw overrides. */
export const useMonthlySalaryMode = ({
  overrides,
  members,
}: UseMonthlySalaryModeParams): MonthlySalaryMode => {
  // Mirrors the per-row switch's disabled rule so the bulk count never promises members the
  // bulk action cannot actually flip.
  const eligibleMemberIds = useMemo(
    () =>
      (members ?? [])
        .filter(
          member => member.canSelectMonthlySalary && !hasBlockingQuickBooksWarnings(member.warnings)
        )
        .map(member => member.id),
    [members]
  );

  const { isEnabledFor, setEnabledFor } = overrides;

  const toggleAll = useCallback(
    (isEnabled: boolean) => setEnabledFor(eligibleMemberIds, isEnabled),
    [eligibleMemberIds, setEnabledFor]
  );

  return {
    ...overrides,
    eligibleMemberIds,
    isAllEnabled: eligibleMemberIds.length > 0 && eligibleMemberIds.every(isEnabledFor),
    toggleAll,
  };
};
